import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Link2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import SEO from "@/components/SEO";
import LanguageToggle from "@/components/LanguageToggle";
import { useAuth } from "@/hooks/useAuth";
import { useI18n } from "@/i18n";
import type { TranslationKey } from "@/i18n/en";

const features: { emoji: string; title: TranslationKey; text: TranslationKey }[] = [
  { emoji: "🔗", title: "home.feature.code.title", text: "home.feature.code.text" },
  { emoji: "🔒", title: "home.feature.lock.title", text: "home.feature.lock.text" },
];

const ShortHome = () => {
  const { user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [url, setUrl] = useState("");

  const handleShorten = (e: React.FormEvent) => {
    e.preventDefault();
    const target = url.trim();
    if (target) sessionStorage.setItem("trimr_pending_url", target);
    navigate(user ? "/tableau-de-bord" : "/creer-un-compte");
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title={t("home.seo.title")}
        description={t("home.seo.description")}
        keywords={t("home.seo.keywords")}
        canonical="https://trimr.cc"
      />

      <header>
        <div className="container mx-auto flex items-center justify-between gap-4 px-4 py-5">
          <Link to="/" className="flex items-center gap-2 font-display text-lg font-bold">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-glow">
              <Link2 className="h-4 w-4" />
            </span>
            {t("brand.name")}
          </Link>
          <nav className="flex items-center gap-2">
            <LanguageToggle />
            {user ? (
              <Button asChild size="sm" className="rounded-lg shadow-glow">
                <Link to="/tableau-de-bord">{t("nav.myLinks")}</Link>
              </Button>
            ) : (
              <>
                <Button asChild variant="ghost" size="sm" className="rounded-lg">
                  <Link to="/connexion">{t("nav.login")}</Link>
                </Button>
                <Button asChild size="sm" className="rounded-lg shadow-glow">
                  <Link to="/creer-un-compte">{t("nav.signup")}</Link>
                </Button>
              </>
            )}
          </nav>
        </div>
      </header>

      <main>
        <section className="container mx-auto px-4 pb-16 pt-16 text-center sm:pt-24">
          <h1 className="mx-auto max-w-3xl text-4xl font-extrabold tracking-tight sm:text-6xl">
            {t("home.hero.title")}
          </h1>
          <p className="mx-auto mt-5 max-w-xl text-lg text-muted-foreground">{t("home.hero.text")}</p>

          <form
            onSubmit={handleShorten}
            className="card-soft mx-auto mt-10 flex max-w-2xl flex-col gap-2 p-2 sm:flex-row"
          >
            <Input
              type="text"
              inputMode="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder={t("home.hero.placeholder")}
              aria-label={t("home.hero.placeholder")}
              className="input-soft h-14 flex-1 text-base"
            />
            <Button type="submit" size="lg" className="h-14 rounded-lg px-6 text-base font-semibold shadow-glow">
              {t("home.cta.first")}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </form>
          {!user && (
            <p className="mt-4 text-sm text-muted-foreground">
              <Link to="/connexion" className="text-primary hover:underline">
                {t("home.cta.haveAccount")}
              </Link>
            </p>
          )}
        </section>

        <section className="container mx-auto grid max-w-3xl gap-6 px-4 pb-24 sm:grid-cols-2">
          {features.map(({ emoji, title, text }) => (
            <div key={title} className="card-soft p-7">
              <span className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-secondary text-2xl">
                {emoji}
              </span>
              <h2 className="text-lg font-bold">{t(title)}</h2>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{t(text)}</p>
            </div>
          ))}
        </section>
      </main>

      <footer className="border-t border-border py-8">
        <div className="container mx-auto flex flex-wrap items-center justify-between gap-3 px-4 text-sm text-muted-foreground">
          <span>
            © {new Date().getFullYear()} {t("brand.name")}
          </span>
          <div className="flex gap-4">
            <Link to="/confidentialite" className="hover:text-foreground">
              {t("nav.privacy")}
            </Link>
            <Link to="/contact" className="hover:text-foreground">
              {t("nav.contact")}
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ShortHome;
